import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { breakpoints, Card, Inline, Notice, Stack, Text, textVariants, vars } from '@etg/wings';
import { PartOfExternalFlights } from '@eti/schema-types';
import { Accordion, Pictogram } from '@eti/components';
import { linkStyles } from '../../../../../../../utils/styleUtils';
import { useOrderDetails } from '../../../../../containers/OrderDetailsContext';
import Bound from './Bound';

const accordionWrapperStyles = css`
  border: 1px solid ${vars.colors.divider};
  border-radius: 3px;
`;

const descriptionStyles = css`
  @media (min-width: ${breakpoints._560}) {
    padding-inline-start: 48px;
  }
`;

const linkPaddingStyles = css`
  padding-inline-start: 28px;

  @media (min-width: ${breakpoints._560}) {
    padding-inline-start: 76px;
  }
`;

interface SelfCheckinProps {
  isInitiallyOpen: boolean;
}

const SelfCheckin = ({ isInitiallyOpen }: SelfCheckinProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { brand } = useSiteContext();
  const { bounds, hasRyanairLCCSupplier, partOfExternalFlights, ryanairLocale } = useOrderDetails();
  const isOrderExternal = partOfExternalFlights !== PartOfExternalFlights.None;

  const url = p('OrderDetails.ManageTrip.InfoAboutRyanair.Url');

  const shouldShowRyanairInfo =
    (p('OrderDetails.ManageTrip.InfoAboutRyanair.Enabled') && hasRyanairLCCSupplier) ||
    isOrderExternal;

  return (
    <Card className={accordionWrapperStyles} id="self-check-in-card">
      <Accordion
        id="self-check-in-accordion"
        isInitiallyExpanded={isInitiallyOpen}
        spacing={16}
        variant="full-width"
      >
        <Accordion.Header data-testid="self-check-in" id="self-check-in">
          <Inline alignY="center" noWrap>
            <Pictogram brand={brand.code} id="self-checkin" size="32px" />
            <Text variant={textVariants._16_BOLD}>{t('OrderDetails.CheckIn.Self.Title')}</Text>
          </Inline>
        </Accordion.Header>
        <Accordion.Body>
          <Stack>
            <Text asHtml className={descriptionStyles}>
              {t('OrderDetails.CheckIn.Self.Description')}
            </Text>
            {shouldShowRyanairInfo && (
              <Stack align="start" data-testid="ryanair-info" spacing={4}>
                <Notice className={descriptionStyles} variant="information">
                  <Text variant={textVariants._14_BOLD}>
                    {t(
                      isOrderExternal
                        ? 'OrderDetails.RyanairFlight.InfoBox.Description'
                        : 'OrderDetails.ManageTrip.InfoAboutRyanair.Description',
                    )}
                  </Text>
                </Notice>
                <a
                  className={cx(linkStyles, linkPaddingStyles)}
                  href={isOrderExternal ? `https://www.ryanair.com/${ryanairLocale}` : url}
                  rel="noreferrer"
                  target="_blank"
                >
                  {t(
                    isOrderExternal
                      ? 'OrderDetails.RyanairFlight.InfoBox.Button.Text'
                      : 'OrderDetails.ManageTrip.InfoAboutRyanair.LinkText',
                  )}
                </a>
              </Stack>
            )}
            <Stack data-testid="self-checkin-itinerary-stack">
              {bounds.map((bound) => (
                <Bound bound={bound} key={`bound_${bound.boundId}`} />
              ))}
            </Stack>
          </Stack>
        </Accordion.Body>
      </Accordion>
    </Card>
  );
};

export default SelfCheckin;
